/* Geladene Modelle (.mdl): Dateien annehmen, Begleitdateien zuordnen (Texturen nameT.mdl, Sequenzgruppen name01.mdl),
 * Modelle lesen und fertige Geometrie zwischenspeichern. Ohne DOM. Schlüssel ist der Dateiname klein geschrieben ("w_medkit.mdl"). */
import { buildGeometry, parseMdl, seqFileOf, textureFileOf } from './mdl.ts';
import type { BuildOpts, MdlFile, MdlGroup } from './mdl.ts';

const baseOf = (p: string): string => String(p || '').replace(/\\/g, '/').replace(/^.*\//, '').toLowerCase();

/** Schlüssel eines Modells aus einem Pfad ("models/W_Medkit.mdl" → "w_medkit.mdl") */
export const modelKey = baseOf;

/** Ist der Name eine Begleitdatei zu einer anderen Datei der Menge (Textur nameT.mdl oder Sequenzgruppe name01.mdl)? */
export function isCompanion(name: string, all: Iterable<string>): boolean {
  const n = baseOf(name), set = new Set(Array.from(all, baseOf));
  const t = /^(.*)t\.mdl$/.exec(n);
  if (t && set.has(t[1] + '.mdl')) return true;
  const g = /^(.*?)(\d\d)\.mdl$/.exec(n);
  return !!g && set.has(g[1] + '.mdl');
}

export class ModelStore {
  private raw = new Map<string, ArrayBuffer>();
  private parsed = new Map<string, MdlFile>();
  private geo = new Map<string, MdlGroup[]>();
  /** Fehler beim Lesen je Schlüssel (letzter Versuch) */
  errors = new Map<string, Error>();

  /** Dateien aufnehmen (auch nachträglich Begleitdateien) und betroffene Modelle neu lesen. Liefert die Schlüssel, die jetzt lesbar sind. */
  add(files: { name: string; buf: ArrayBuffer }[]): string[] {
    for (const f of files) this.raw.set(baseOf(f.name), f.buf);
    this.geo.clear();
    const touched = new Set<string>();
    for (const f of files) {
      const k = baseOf(f.name);
      // Begleitdateien betreffen ihr Hauptmodell
      const t = /^(.*)t\.mdl$/.exec(k), g = /^(.*?)(\d\d)\.mdl$/.exec(k);
      if (t && this.raw.has(t[1] + '.mdl')) touched.add(t[1] + '.mdl');
      else if (g && this.raw.has(g[1] + '.mdl')) touched.add(g[1] + '.mdl');
      else touched.add(k);
    }
    const ok: string[] = [];
    for (const k of touched) {
      try { this.parsed.set(k, this.read(k)); this.errors.delete(k); ok.push(k); }
      catch (e) { this.parsed.delete(k); this.errors.set(k, e as Error); }
    }
    return ok;
  }

  private read(key: string): MdlFile {
    const buf = this.raw.get(key);
    if (!buf) throw new Error('fehlt');
    const tex = this.raw.get(baseOf(textureFileOf(key)));
    return parseMdl(buf, tex);
  }

  has(key: string): boolean { return this.parsed.has(baseOf(key)); }
  get(key: string): MdlFile | null { return this.parsed.get(baseOf(key)) || null; }
  /** Schlüssel der lesbaren Modelle */
  keys(): string[] { return Array.from(this.parsed.keys()); }
  clear(): void { this.raw.clear(); this.parsed.clear(); this.geo.clear(); this.errors.clear(); }

  /** Dreiecke eines Modells in der gewählten Stellung; null, wenn es nicht geladen ist. Das Ergebnis wird zwischengespeichert. */
  geometry(key: string, opts: BuildOpts = {}): MdlGroup[] | null {
    const k = baseOf(key), m = this.parsed.get(k);
    if (!m) return null;
    const id = k + '|' + (opts.body || 0) + '|' + (opts.skin || 0) + '|' + (opts.seq || 0) + '|' + (opts.frame || 0);
    let g = this.geo.get(id);
    if (!g) {
      const seqFiles: (ArrayBuffer | undefined)[] = [];
      m.seqGroups.forEach((_s, i) => { if (i > 0) seqFiles[i] = this.raw.get(baseOf(seqFileOf(k, i))); });
      g = buildGeometry(m, { ...opts, seqFiles });
      this.geo.set(id, g);
    }
    return g;
  }
}
